import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import locale from 'date-fns/locale/es'
import cx from 'classnames'
import styles from './CancelGroupEventModal.module.scss'

const fmtShortDate = (d) => {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(2)
  return `${dd}/${mm}/${yy}`
}

const fmtLongDate = (day) => {
  try {
    const d = new Date(`${day}T00:00`)
    const str = format(d, "EEEE d 'de' MMMM", { locale })
    return str.charAt(0).toUpperCase() + str.slice(1)
  } catch {
    return day
  }
}

const CancelGroupEventModal = ({
  show,
  event,
  groupEvents = [],
  loading,
  onClose,
  onCancelThis,
  onCancelFuture,
  onViewSession,
}) => {
  const { t } = useTranslation()
  const [selection, setSelection] = useState('this')

  const freqLabels = {
    weekly:   t('frequency_weekly',   'Semanal'),
    biweekly: t('frequency_biweekly', 'Quincenal'),
    monthly:  t('frequency_monthly',  'Mensual'),
  }

  useEffect(() => {
    if (show) setSelection('this')
  }, [show])

  if (!show) return null

  const sortedActive = [...groupEvents]
    .filter(e => e.status !== 'cancelled')
    .sort((a, b) => a.day.localeCompare(b.day) || a.time.localeCompare(b.time))

  const currentIndex = sortedActive.findIndex(e => e.id === event?.id)
  const totalCount = sortedActive.length
  const sessionNum = currentIndex >= 0 ? currentIndex + 1 : null

  const remainingEvents = currentIndex >= 0 ? sortedActive.slice(currentIndex) : []
  const remainingCount = remainingEvents.length

  const freqLabel = freqLabels[event?.recurrence?.frequency] ?? null
  const firstRemaining = remainingEvents[0]
  const lastRemaining = remainingEvents[remainingEvents.length - 1]
  const remainingDateRange = firstRemaining && lastRemaining && firstRemaining !== lastRemaining
    ? `${fmtShortDate(new Date(`${firstRemaining.day}T00:00`))} → ${fmtShortDate(new Date(`${lastRemaining.day}T00:00`))}`
    : null

  const modal = (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <div className={styles.card} onClick={e => e.stopPropagation()}>

          <div className={styles.header}>
            <h3 className={styles.title}>{t('cancel_event', 'Cancelar turno')}</h3>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('close', 'Cerrar')}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          <div className={styles.optionList}>
            <button
              type="button"
              className={cx(styles.optionItem, { [styles.optionItemSelected]: selection === 'this' })}
              onClick={() => setSelection('this')}
            >
              <div className={styles.optionLeft}>
                <span className={cx('material-symbols-outlined', styles.radioIcon, { [styles.radioIconSelected]: selection === 'this' })}>
                  {selection === 'this' ? 'radio_button_checked' : 'radio_button_unchecked'}
                </span>
                <div className={styles.optionText}>
                  <p className={styles.optionTitle}>{t('only_this_event', 'Solo este turno')}</p>
                  {sessionNum !== null && (
                    <p className={styles.optionSub}>{t('session_n_of_total', 'Sesión {{n}}/{{total}}', { n: sessionNum, total: totalCount })}</p>
                  )}
                </div>
              </div>
              <div className={styles.optionRight}>
                {event?.day && <p className={styles.optionRightMain}>{fmtLongDate(event.day)}</p>}
                {event?.time && <p className={styles.optionRightSub}>{event.time} {t('hours_suffix', 'hs')}</p>}
              </div>
            </button>

            <button
              type="button"
              className={cx(styles.optionItem, { [styles.optionItemSelected]: selection === 'future' })}
              onClick={() => setSelection('future')}
            >
              <div className={styles.optionLeft}>
                <span className={cx('material-symbols-outlined', styles.radioIcon, { [styles.radioIconSelected]: selection === 'future' })}>
                  {selection === 'future' ? 'radio_button_checked' : 'radio_button_unchecked'}
                </span>
                <div className={styles.optionText}>
                  <p className={styles.optionTitle}>{t('all_remaining_sessions', 'Todas las sesiones restantes')}</p>
                  <p className={styles.optionSub}>
                    {remainingCount === 1
                      ? t('one_remaining_session', '1 sesión restante')
                      : t('n_remaining_sessions', '{{n}} sesiones restantes', { n: remainingCount })}
                  </p>
                </div>
              </div>
              <div className={styles.optionRight}>
                {freqLabel && <p className={styles.optionRightMain}>{freqLabel}</p>}
                {remainingDateRange && <p className={styles.optionRightSub}>{remainingDateRange}</p>}
              </div>
            </button>
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.btnViewSession} onClick={onViewSession}>
              {t('view_session', 'Ver sesión')}
            </button>
            <button
              type="button"
              className={styles.btnCancelConfirm}
              disabled={loading}
              onClick={() => selection === 'this' ? onCancelThis?.() : onCancelFuture?.()}
            >
              {loading && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
              {t('cancel', 'Cancelar')}
            </button>
          </div>

        </div>
      </div>
    </>
  )

  return createPortal(modal, document.body)
}

export default CancelGroupEventModal
